import type { ColTypeDef } from 'ag-grid-community';
import { agMultiColumnFilter } from 'components/shared/Blotter/Grid/Hooks/utils';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { StatusToColorMapper } from '../CellRenderers';
import { makeStatusCellRenderer } from '../CellRenderers';

export function useStatusColumnType<Status extends string | boolean | null | undefined, T>(
  colorMapper: StatusToColorMapper<Status>,
  useSetColumnFilter?: boolean,
  handleValuesUpdate: boolean = false,
  textFormatter?: (value: Status) => string,
): ColTypeDef<T> {
  return useMemo(() => {
    const filter = useSetColumnFilter ? agMultiColumnFilter(handleValuesUpdate) : { filter: 'agTextColumnFilter' };
    return {
      cellRenderer: makeStatusCellRenderer<Status>(colorMapper, textFormatter),
      ...filter,
    };
  }, [colorMapper, handleValuesUpdate, textFormatter, useSetColumnFilter]);
}

export function useServerSideStatusColumnType<T, S extends string = string>(
  colorMapper: StatusToColorMapper<S>,
  textFormatter?: (value: S) => string,
): ColTypeDef<T> {
  return useMemo(
    () => ({
      cellRenderer: makeStatusCellRenderer<S>(colorMapper, textFormatter),
    }),
    [colorMapper, textFormatter],
  );
}

export function useYesNoColumnType<T>() {
  const { formatMessage } = useIntl();
  const yesNoColorMapper = useCallback((status: boolean) => (status ? 'success' : 'secondary'), []);
  const yesNoTextFormatter = useCallback(
    (status: boolean) => (status ? formatMessage({ id: 'yes' }) : formatMessage({ id: 'no' })),
    [formatMessage],
  );
  const column = useStatusColumnType<boolean, T>(yesNoColorMapper, false, false, yesNoTextFormatter);
  return column;
}
